import { Show } from "shared/components";
import { has } from "shared/utils";
import { VerificationNames, VerificationStatus } from "shared/types";
import {
  VerificationSchemaNames,
  VerificationSettings
} from "types/landlord/verificationTemplate";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import {
  ApplicantOrInvitee,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import { LandLordVerificationStatusBadge } from "../LandLordVerificationStatusBadge";
import { shouldShowLandlordVerification } from "views/applications-id/components/ApplicantsTable/utils";
import { useAuthContext } from "providers/Auth";
import { DataWithAuditFlag } from "./components/DataWithAuditFlag";
import { Applicant } from "types/application";

export const HousingHistorySection = ({
  applicant,
  verification,
  hasAuditFlagEnabled,
  verificationSchemas
}: {
  verification: Verification;
  hasAuditFlagEnabled: boolean;
  applicant: ApplicantOrInvitee;
  verificationSchemas?: VerificationSchemaNames;
}) => {
  const { permissions } = useAuthContext();

  const auditSummaryFlags =
    "audit_summary_flags" in applicant
      ? applicant.audit_summary_flags
      : undefined;

  const verificationsList =
    "verifications" in applicant
      ? (applicant.verifications as Array<{
          name: VerificationNames;
          status: VerificationStatus;
        }>)
      : [];

  const premiumRentalHistoryVerificationStatus = verificationsList.find(
    (v) => v.name === VerificationNames.PremiumRentalHistory
  )?.status;

  const premiumRentalHistoryExecutionMode = (
    verificationSchemas?.[VerificationNames.PremiumRentalHistory] as
      | VerificationSettings
      | undefined
  )?.execution_mode;

  const isPremiumFlow = Boolean(
    verificationSchemas?.[VerificationNames.PremiumRentalHistory]
      ?.premium_rental_history_enabled
  );

  // When premium rental history is enabled, the badge should reflect the
  // PremiumRentalHistory verification (status + execution mode) rather than the
  // rental verification object passed in via props.
  const badgeVerification: Verification = isPremiumFlow
    ? {
        ...verification,
        name: VerificationNames.PremiumRentalHistory,
        status: premiumRentalHistoryVerificationStatus ?? verification.status,
        settings: {
          ...verification.settings,
          execution_mode: premiumRentalHistoryExecutionMode
        }
      }
    : verification;

  return (
    <>
      <Cell
        isDataCell
        className="relative"
        isPermitted={permissions.canAccessReports.housingHistoryReport}
        showRestrictedFlag={!permissions.canAccessReports.housingHistoryReport}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary?.total_number_of_residences)}
          dataValue={verification.summary?.total_number_of_residences ?? null}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
        <ReportStatusBadge
          verification={badgeVerification}
          applicant={applicant as Applicant}
        />
      </Cell>
      <Cell
        isDataCell
        isPermitted={permissions.canAccessReports.housingHistoryReport}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary?.average_monthly_payment_amount)}
          dataValue={
            has(verification.summary?.average_monthly_payment_amount)
              ? `$${verification.summary.average_monthly_payment_amount}`
              : null
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
      <Cell
        isDataCell
        isPermitted={permissions.canAccessReports.housingHistoryReport}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary?.total_months_of_residence)}
          dataValue={verification.summary?.total_months_of_residence ?? null}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>

      <Show if={shouldShowLandlordVerification(verificationSchemas)}>
        <Cell
          isDataCell
          isPermitted={permissions.canAccessReports.housingHistoryReport}
        >
          <LandLordVerificationStatusBadge
            verification={badgeVerification}
            applicant={applicant as Applicant}
            isPremiumFlow={isPremiumFlow}
          />
        </Cell>
      </Show>
    </>
  );
};
